/**
 * Build identity (INS-1968). This bundle is static on Cloudflare, so a tab left
 * open across a deploy keeps running the old code, and the access data it
 * cached in localStorage at login keeps gating the old feature set. Two
 * questions answer both halves:
 *
 *   - Is the build being SERVED newer than the one RUNNING? (`version.json`
 *     poll → useReloadOnStaleBuild reloads on the next page change.)
 *   - Is the build now running different from the one that last booted in
 *     this browser? (`consumeBuildChange` → the access refresh gate re-fetches
 *     orgs/roles/permissions instead of trusting the cached atoms.)
 */

/** Identity of the running bundle, injected by `define` in vite.config.ts. */
export const APP_BUILD_ID: string = __APP_BUILD_ID__;

export const VERSION_MANIFEST_PATH = "/version.json";

/** Build the last boot in this browser ran. localStorage keys carry `MEQ-`. */
export const BOOTED_BUILD_ID_KEY = "MEQ-booted-build-id";

/**
 * Build id the deployed site currently serves, or null when it cannot be
 * determined (offline, 404 in an environment that never emitted the file, a
 * malformed body). Null never counts as a change — a failed poll must not
 * reload anyone.
 */
export async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const response = await fetch(VERSION_MANIFEST_PATH, { cache: "no-store" });
    if (!response.ok) return null;
    const body = (await response.json()) as { buildId?: unknown };
    return typeof body.buildId === "string" && body.buildId !== ""
      ? body.buildId
      : null;
  } catch {
    return null;
  }
}

/** True when the served build is known and is not the one running here. */
export function isDeployedBuildStale(deployedBuildId: string | null): boolean {
  return deployedBuildId != null && deployedBuildId !== APP_BUILD_ID;
}

function readBootedBuildId(): string | null {
  try {
    return window.localStorage.getItem(BOOTED_BUILD_ID_KEY);
  } catch {
    return null;
  }
}

function writeBootedBuildId(buildId: string): void {
  try {
    window.localStorage.setItem(BOOTED_BUILD_ID_KEY, buildId);
  } catch {
    // Storage blocked (private mode, cookies off). The next load will look
    // like a build change and refresh access once more — harmless.
  }
}

let buildChangeSinceLastBoot: boolean | undefined;

/**
 * Whether this page load is the first on this build since the browser last
 * booted the app. Decided once per page load (first caller wins, later callers
 * get the same answer) and records the running build as booted as a side
 * effect, so the next load compares against this one. A browser with no
 * record (first ever visit, cleared storage) reports no change: there is
 * nothing cached to be stale.
 */
export function consumeBuildChange(): boolean {
  if (buildChangeSinceLastBoot === undefined) {
    const previous = readBootedBuildId();
    buildChangeSinceLastBoot = previous != null && previous !== APP_BUILD_ID;
    if (previous !== APP_BUILD_ID) writeBootedBuildId(APP_BUILD_ID);
  }
  return buildChangeSinceLastBoot;
}

/**
 * Record that a from-scratch boot ran on this build: everything it caches was
 * just fetched here, so no gate mounted later in this page load (Login →
 * first private route) owes a refresh, and neither does the next load.
 */
export function markBuildBooted(): void {
  buildChangeSinceLastBoot = false;
  writeBootedBuildId(APP_BUILD_ID);
}

/** Test-only: forget the per-page-load decision between cases. */
export function __resetAppBuildForTests(): void {
  buildChangeSinceLastBoot = undefined;
}
